<template>
  <div class="max-w-3xl mx-auto space-y-6">
    <!-- Header -->
    <div class="text-center space-y-3">
      <h2 class="text-2xl font-bold text-slate-900 dark:text-white">
        {{ $t('AI_ONBOARDING.BOT_MODES.TITLE') || 'Bot của bạn sẽ làm gì?' }}
      </h2>
      <p class="text-sm text-slate-600 dark:text-slate-400">
        {{ $t('AI_ONBOARDING.BOT_MODES.SUBTITLE') || 'Chọn chế độ hoạt động phù hợp với business của bạn' }}
      </p>
    </div>

    <!-- Mode Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
      <button
        v-for="mode in modeOptions"
        :key="mode.key"
        class="group relative w-full text-left p-4 rounded-2xl border transition-all overflow-hidden backdrop-blur h-full"
        :class="cardClass(mode.key)"
        @click="toggleMode(mode.key)"
      >
        <div
          class="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          :class="glowClass(mode.key)"
        />

        <div class="relative"> 
          <div class="space-y-2">
            <div class="flex items-center gap-2 flex-wrap">
              <h3 class="text-lg font-semibold text-slate-900 dark:text-white">
                {{ mode.emoji }} {{ mode.title }}
              </h3>
            </div>
            
            <p class="text-sm text-slate-600 dark:text-slate-200">
              {{ mode.description }}
            </p>
            
            <div class="mt-3 flex flex-col gap-2">
              <span
                v-for="feature in mode.features"
                :key="feature"
                class="text-xs px-2.5 py-1 rounded-lg border font-medium w-fit"
                :class="modeStyles[mode.key].feature"
              >
                {{ feature }}
              </span>
            </div>
            
            <div
              class="mt-4 px-3 py-2 rounded-lg border flex items-start gap-2 min-h-[4rem]"
              :class="modeStyles[mode.key].bestFor"
            >
              <lucide-icon name="sparkles" :size="16" class="mt-0.5 shrink-0" />
              <p class="text-xs font-semibold leading-relaxed">
                {{ mode.bestFor }}
              </p>
            </div>
          </div>

          <div 
            v-if="selectedModes[mode.key]" 
            class="absolute top-2 right-2"
          >
            <lucide-icon name="check-circle-big" :size="20" class="text-grass-500 dark:text-grass-400" />
          </div>
        </div>
      </button>
    </div>

    <!-- Recommendation Box -->
    <div class="p-4 bg-gradient-to-r from-purple-50 via-white to-blue-50 dark:from-slate-800/60 dark:via-slate-800/40 dark:to-slate-800/60 rounded-xl border border-purple-200 dark:border-slate-700/80 shadow-sm">
      <div class="flex items-start gap-3">
        <lucide-icon name="lightbulb" :size="20" class="text-yellow-500 dark:text-yellow-400 shrink-0 mt-0.5" />
        <div>
          <p class="text-sm font-semibold text-purple-900 dark:text-n-gray-3">
            {{ $t('AI_ONBOARDING.BOT_MODES.RECOMMENDATION_TITLE') || 'Khuyến nghị' }}
          </p>
          <p class="text-xs text-purple-900 dark:text-n-gray-3 mt-1 leading-relaxed">
            {{ $t('AI_ONBOARDING.BOT_MODES.RECOMMENDATION_TEXT') || 'Bật CẢ HAI chế độ để bot linh hoạt hơn. Bot sẽ tự động chuyển đổi dựa trên ngữ cảnh cuộc hội thoại!' }}
          </p>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import LucideIcon from 'dashboard/components/LucideIcon.vue';

export default {
  name: 'AiBotModesStep',
  
  components: {
    LucideIcon,
  },

  emits: ['next', 'back'],

  data() {
    return {
      selectedModes: {
        productConsulting: true,
        customerSupport: true,
      },
      modeStyles: {
        productConsulting: {
          selectedCard: 'border-hilab dark:border-hilab-dark bg-gradient-to-br from-fuchsia-50 to-purple-50 dark:from-slate-800/80 dark:to-slate-800/60 shadow-lg hover:shadow-xl hover:-translate-y-0.5',
          idleCard: 'border-slate-200/60 dark:border-slate-700/80 bg-white/80 dark:bg-slate-800/40 opacity-50 hover:opacity-75 hover:border-fuchsia-200 dark:hover:border-slate-600 hover:shadow-md hover:-translate-y-0.5',
          chip: 'bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white',
          icon: 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200',
          iconSelected: 'bg-gradient-to-br from-fuchsia-500 to-purple-500 text-white',
          feature: 'bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200 border-emerald-100 dark:border-emerald-700/50',
          bestFor: 'bg-purple-50 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 border-purple-100 dark:border-purple-700/50',
          glow: 'bg-gradient-to-r from-fuchsia-200/60 via-transparent to-purple-200/60 dark:from-fuchsia-700/20 dark:via-transparent dark:to-purple-700/20',
        },
        customerSupport: {
          selectedCard: 'border-hilab dark:border-hilab-dark bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-slate-800/80 dark:to-slate-800/60 shadow-lg hover:shadow-xl hover:-translate-y-0.5',
          idleCard: 'border-slate-200/60 dark:border-slate-700/80 bg-white/80 dark:bg-slate-800/40 opacity-50 hover:opacity-75 hover:border-blue-200 dark:hover:border-slate-600 hover:shadow-md hover:-translate-y-0.5',
          chip: 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white',
          icon: 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200',
          iconSelected: 'bg-gradient-to-br from-blue-500 to-cyan-500 text-white',
          feature: 'bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200 border-emerald-100 dark:border-emerald-700/50',
          bestFor: 'bg-purple-50 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 border-purple-100 dark:border-purple-700/50',
          glow: 'bg-gradient-to-r from-blue-200/60 via-transparent to-cyan-200/60 dark:from-blue-700/20 dark:via-transparent dark:to-cyan-700/20',
        },
      },
    };
  },

  computed: {
    ...mapState('aiOnboarding', ['botModes']),

    modeOptions() {
      return [
        {
          key: 'productConsulting',
          icon: 'shopping-bag',
          emoji: '',
          title: this.$t('AI_ONBOARDING.BOT_MODES.CONSULTING_TITLE') || 'Tư vấn bán hàng',
          description: this.$t('AI_ONBOARDING.BOT_MODES.CONSULTING_DESC') || 'Bot sẽ hỏi thêm để hiểu rõ nhu cầu khách hàng, sau đó gợi ý sản phẩm phù hợp và upsell thông minh.',
          features: [
            this.$t('AI_ONBOARDING.BOT_MODES.CONSULTING_FEATURE_1') || '✓ Hỏi làm rõ nhu cầu',
            this.$t('AI_ONBOARDING.BOT_MODES.CONSULTING_FEATURE_2') || '✓ Gợi ý sản phẩm',
            this.$t('AI_ONBOARDING.BOT_MODES.CONSULTING_FEATURE_3') || '✓ Upsell thông minh',
          ],
          bestFor: this.$t('AI_ONBOARDING.BOT_MODES.CONSULTING_BEST_FOR') || 'Phù hợp: Shop thời trang, F&B, Thương mại điện tử, Bất động sản',
        },
        {
          key: 'customerSupport',
          icon: 'headphones',
          emoji: '',
          title: this.$t('AI_ONBOARDING.BOT_MODES.SUPPORT_TITLE') || 'Hỗ trợ khách hàng',
          description: this.$t('AI_ONBOARDING.BOT_MODES.SUPPORT_DESC') || 'Bot sẽ xác định vấn đề của khách hàng, sau đó cung cấp hướng dẫn từng bước để giải quyết thắc mắc.',
          features: [
            this.$t('AI_ONBOARDING.BOT_MODES.SUPPORT_FEATURE_1') || '✓ Xác định vấn đề',
            this.$t('AI_ONBOARDING.BOT_MODES.SUPPORT_FEATURE_2') || '✓ Hướng dẫn từng bước',
            this.$t('AI_ONBOARDING.BOT_MODES.SUPPORT_FEATURE_3') || '✓ Giải quyết thắc mắc',
          ],
          bestFor: this.$t('AI_ONBOARDING.BOT_MODES.SUPPORT_BEST_FOR') || 'Phù hợp: Dịch vụ, CSKH, Support, SaaS, Công nghệ',
        },
      ];
    },
    
    canProceed() {
      return this.selectedModes.productConsulting || this.selectedModes.customerSupport;
    },
  },

  mounted() {
    // Load from store if exists
    if (this.botModes.productConsulting !== undefined) {
      this.selectedModes = { ...this.botModes };
    }
  },

  methods: {
    ...mapActions('aiOnboarding', ['updateBotModes']),

    cardClass(modeKey) {
      const palette = this.modeStyles[modeKey];
      return this.selectedModes[modeKey] ? palette.selectedCard : palette.idleCard;
    },

    iconWrapperClass(modeKey) {
      const palette = this.modeStyles[modeKey];
      return this.selectedModes[modeKey] ? palette.iconSelected : palette.icon;
    },

    iconColorClass(modeKey) {
      return this.selectedModes[modeKey] ? 'text-white' : 'text-slate-700 dark:text-slate-200';
    },

    glowClass(modeKey) {
      const baseGlow = this.modeStyles[modeKey].glow;
      // Ẩn glow trong dark mode khi hover card được select
      if (this.selectedModes[modeKey]) {
        return `${baseGlow} dark:group-hover:opacity-0`;
      }
      return baseGlow;
    },

    toggleMode(mode) {
      this.selectedModes[mode] = !this.selectedModes[mode];
    },

    async handleNext() {
      if (!this.canProceed) return;

      await this.updateBotModes(this.selectedModes);
      this.$emit('next');
    },
  },
};
</script>
